import { assertNever } from "common/Support";
import * as deepmerge from "deepmerge";
import tinycolor from "tinycolor2";
import { Color, BaseTheme, randomTheme } from "common/BaseTheme";
import { darkOverlay } from "./theme-overlays/dark-theme-overlay";
import { androidOverlay } from "./theme-overlays/android-theme-overlay";
import { colorOverlay, colorDarkOverlay } from "./theme-overlays/color-theme-overlay";
import { OverlayTheme } from "./overlay-theme";
import { pureBlackOverlay } from "./theme-overlays/pure-black-theme-overlay";
import { pureWhiteOverlay } from "./theme-overlays/pure-white-theme-overlay";
import { mergeTheme } from "sharedUI/lib/theme-utils";
import { defaultTheme, RuntimeTheme as OriginalRuntimeTheme } from "./default-theme";
import { androidTabletOverlay, desktopOverlay, tabletOverlay } from "./tablet-overlay";
import { isOSThemeDark } from "sharedUI/lib/color-theme-utils";
import { isPlayer } from "../../../routes";
import { DeviceFormFactor } from "common/render/form-factor";
export { pageMarginMedium } from "./default-theme";

export const mobileSystemFontFamily = "-apple-system,BlinkMacSystemFont,Roboto,sans-serif";

export const primaryColors = Object.keys(Color).map(name => ({ name, value: (Color as any)[name] }));
const types = ["modern", "editorial", "future", "system"];
export const typographyTypes = types.map(type => ({
    name: type.slice(0, 1).toUpperCase() + type.slice(1),
    value: type,
}));

export type RuntimeTheme = OriginalRuntimeTheme;

let currentThemeCache: RuntimeTheme | undefined;
let initialsFontColorCache: string | undefined;
let initialsBackgroundCache: string | undefined;

export function initialsFontColor(theme: RuntimeTheme): string {
    // must use any here or we break typing
    if (theme === currentThemeCache && initialsFontColorCache !== undefined) {
        return initialsFontColorCache;
    }

    const result = tinycolor(theme.primaryAccentColor).darken(theme.listItem.textDarkenAmount).toHexString();
    currentThemeCache = theme;
    initialsFontColorCache = result;
    return result;
}

export function initialsBackground(theme: RuntimeTheme): string {
    // must use any here or we break typing
    if (theme === currentThemeCache && initialsBackgroundCache !== undefined) {
        return initialsBackgroundCache;
    }

    const result = tinycolor(theme.primaryAccentColor).desaturate().setAlpha(0.12).toRgbString();
    currentThemeCache = theme;
    initialsBackgroundCache = result;
    return result;
}

export type ThemePlatform = "iOS" | "Android";

export function getThemeForPlatform(
    appTheme?: BaseTheme,
    platform?: ThemePlatform,
    deviceFormFactor?: DeviceFormFactor,
    darkMode?: boolean
): RuntimeTheme {
    currentThemeCache = undefined;
    initialsFontColorCache = undefined;
    initialsBackgroundCache = undefined;

    if (platform === undefined) {
        platform = "iOS";
    }

    const adaptive = appTheme?.themeIsAdaptive === true && isPlayer();
    const isOSDark: boolean = darkMode ?? isOSThemeDark();

    let overlay: OverlayTheme | undefined;
    if (appTheme !== undefined) {
        switch (appTheme.themeOverlay) {
            case "dark":
                overlay = darkOverlay;
                if (!isOSDark && adaptive) {
                    overlay = undefined;
                }
                break;
            case "pureblack":
                overlay = pureBlackOverlay;
                if (!isOSDark && adaptive) {
                    overlay = pureWhiteOverlay;
                }
                break;
            case "color":
                overlay = colorOverlay(appTheme);
                if (isOSDark && adaptive) {
                    overlay = colorDarkOverlay(appTheme);
                }
                break;
            case "colorDark":
                overlay = colorDarkOverlay(appTheme);
                if (!isOSDark && adaptive) {
                    overlay = colorOverlay(appTheme);
                }
                break;
            case "purewhite":
                overlay = pureWhiteOverlay;
                if (isOSDark && adaptive) {
                    overlay = pureBlackOverlay;
                }
                break;
            default:
                if (isOSDark && adaptive) {
                    overlay = darkOverlay;
                }
        }

        if (appTheme.themeOverlay === "dark" || appTheme.themeOverlay === "pureblack") {
            // <meta name="apple-mobile-web-app-status-bar-style" content="default" />
            const meta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
            if (meta !== null) {
                meta.setAttribute("content", "black");
            }
        } else if (appTheme.themeOverlay === "color" || appTheme.themeOverlay === "colorDark") {
            const meta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
            if (meta !== null) {
                meta.setAttribute("content", "black-translucent");
            }
        }
    }

    const baseTheme = randomTheme("none");
    if (appTheme === undefined) {
        appTheme = baseTheme;
    }

    const runtimeTheme = deepmerge.all([baseTheme, appTheme, defaultTheme]) as RuntimeTheme;
    const overlays = [overlay];
    if (deviceFormFactor === DeviceFormFactor.Tablet) {
        if (platform === "Android") {
            overlays.push(androidTabletOverlay);
        } else {
            overlays.push(tabletOverlay);
        }
    } else if (deviceFormFactor === DeviceFormFactor.Desktop) {
        overlays.push(desktopOverlay);
    }

    switch (platform) {
        case "iOS":
            return mergeTheme(runtimeTheme, [...overlays]);
        case "Android":
            return mergeTheme(runtimeTheme, [androidOverlay, ...overlays]);
        default:
            assertNever(platform);
    }
    throw new Error("Hell froze over");
}

export function isDarkTheme(theme: { themeOverlay?: string }): boolean {
    return theme.themeOverlay === "dark" || theme.themeOverlay === "pureblack" || theme.themeOverlay === "colorDark";
}
